import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import clsx from 'clsx';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * D3ChartBase - Base component for all D3 charts
 * Provides common functionality: SVG container, tooltip, responsive behavior, theme integration
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data
 * @param {Function} props.draw - Function to draw the chart, receives (svg, data, dimensions, theme)
 * @param {Object} props.margin - Chart margins {top, right, bottom, left}
 * @param {number} props.height - Chart height (default: 300)
 * @param {string} props.className - Additional CSS classes
 */
export function D3ChartBase({
  data = [],
  draw,
  margin = { top: 20, right: 20, bottom: 40, left: 50 },
  height = 300,
  className = '',
  chartName = 'Unknown Chart' // Add name for debugging
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const prevDepsRef = useRef({ data: null, margin: null, height: null, theme: null });
  const renderCountRef = useRef(0);
  const { theme: currentTheme } = useTheme();

  useEffect(() => {
    if (!containerRef.current || !data.length || !draw) return;

    // Track what changed to trigger this render
    renderCountRef.current += 1;
    const now = new Date();
    const timestamp = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;

    const changedDeps = [];
    if (prevDepsRef.current.data !== data) changedDeps.push('data');
    if (prevDepsRef.current.height !== height) changedDeps.push('height');
    if (prevDepsRef.current.theme !== currentTheme) changedDeps.push('theme');

    // Skip redundant renders (React.StrictMode double-invocation with no actual changes)
    if (changedDeps.length === 0 && prevDepsRef.current.data !== null) {
      console.log(`[${timestamp}] ⏭️  Chart: "${chartName}" | Skipping redundant render (no changes)`);
      return;
    }

    prevDepsRef.current = { data, height, theme: currentTheme };

    // Render synchronously to prevent layout shift
    // CSS variables are available immediately via getComputedStyle
    if (!containerRef.current) return;

    // Get container width for responsive charts
    const containerWidth = containerRef.current.offsetWidth;

    // Skip render if container hasn't been laid out yet (width is 0)
    // This prevents rendering with incorrect dimensions during initial mount
    if (containerWidth === 0) {
      console.log(`[${timestamp}] ⏸️  Chart: "${chartName}" | Skipping render (container width is 0, waiting for layout)`);
      return;
    }

    const width = containerWidth - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove();

    // Create SVG with responsive viewBox
    const svg = d3.select(svgRef.current)
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${containerWidth} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Get theme colors from CSS variables
    const theme = getThemeColors();

    // Call the draw function with all necessary parameters
    const dimensions = { width, height: chartHeight, margin };
    const shouldAnimate = !hasAnimatedRef.current;

    // Detailed logging
    console.log(`[${timestamp}] 🎨 Chart: "${chartName}" | Render #${renderCountRef.current} | shouldAnimate: ${shouldAnimate} | data: ${data.length} items | Changed: [${changedDeps.join(', ') || 'initial'}] | hasAnimated: ${hasAnimatedRef.current}`);

    // Mark as animated BEFORE drawing to prevent rapid re-renders from animating twice
    if (shouldAnimate) {
      hasAnimatedRef.current = true;
    }

    draw(svg, data, dimensions, theme, tooltipRef.current, shouldAnimate);
  }, [data, height, currentTheme, chartName]); // Removed 'draw' and 'margin' - they never actually change

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current || !data.length || !draw) return;

      requestAnimationFrame(() => {
        if (!containerRef.current) return;

        const containerWidth = containerRef.current.offsetWidth;
        const width = containerWidth - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;

        d3.select(svgRef.current)
          .attr('viewBox', `0 0 ${containerWidth} ${height}`);

        // Redraw chart with new dimensions
        d3.select(svgRef.current).selectAll('*').remove();

        const svg = d3.select(svgRef.current)
          .attr('width', '100%')
          .attr('height', height)
          .attr('viewBox', `0 0 ${containerWidth} ${height}`)
          .attr('preserveAspectRatio', 'xMidYMid meet')
          .append('g')
          .attr('transform', `translate(${margin.left},${margin.top})`);

        const theme = getThemeColors();
        const dimensions = { width, height: chartHeight, margin };
        // Don't animate on resize - only on initial mount
        draw(svg, data, dimensions, theme, tooltipRef.current, false);
      });
    };

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 250);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimer);
    };
  }, [data, draw, margin, height, currentTheme]);

  return (
    <div
      ref={containerRef}
      className={clsx('w-full h-full relative', className)}
      style={{ willChange: 'contents' }}
    >
      <svg
        ref={svgRef}
        className="overflow-visible"
        style={{ willChange: 'transform, opacity' }}
      />
      <D3Tooltip ref={tooltipRef} />
    </div>
  );
}

/**
 * D3Tooltip - Reusable tooltip component for D3 charts
 */
export const D3Tooltip = React.forwardRef((props, ref) => {
  return (
    <div
      ref={ref}
      className="fixed pointer-events-none opacity-0 transition-opacity duration-200 z-50"
      style={{ left: 0, top: 0 }}
    >
      <div className="bg-base-100 text-base-content border border-base-300 rounded-lg shadow-lg p-3 text-sm">
        <div className="tooltip-content"></div>
      </div>
    </div>
  );
});

D3Tooltip.displayName = 'D3Tooltip';

/**
 * Get theme colors from CSS variables
 * Returns OKLCH color values from the current theme
 */
function getThemeColors() {
  const root = document.documentElement;
  const getColor = (varName) => {
    const value = getComputedStyle(root).getPropertyValue(`--color-${varName}`).trim();
    return value ? `oklch(${value})` : null;
  };

  // Helper to get color with alpha/opacity
  const getColorWithAlpha = (varName, alpha) => {
    const value = getComputedStyle(root).getPropertyValue(`--color-${varName}`).trim();
    return value ? `oklch(${value} / ${alpha})` : null;
  };

  return {
    // Base colors
    base100: getColor('base-100'),
    base200: getColor('base-200'),
    base300: getColor('base-300'),
    baseContent: getColor('base-content'),

    // Brand colors
    primary: getColor('primary'),
    primaryContent: getColor('primary-content'),
    secondary: getColor('secondary'),
    accent: getColor('accent'),

    // Semantic colors
    success: getColor('success'),
    warning: getColor('warning'),
    error: getColor('error'),
    info: getColor('info'),

    // Confidence colors (Tamil-specific)
    confidenceLearned: getColor('confidence-learned'),
    confidenceLearnedBg: getColor('confidence-learned-bg'),
    confidenceRecognised: getColor('confidence-recognised'),
    confidenceRecognisedBg: getColor('confidence-recognised-bg'),
    confidenceFamiliar: getColor('confidence-familiar'),
    confidenceFamiliarBg: getColor('confidence-familiar-bg'),
    confidenceNew: getColor('confidence-new'),
    confidenceNewBg: getColor('confidence-new-bg'),

    // Utility function to get any color with alpha
    withAlpha: getColorWithAlpha,
  };
}

/**
 * Utility functions for D3 charts
 */

/**
 * Show tooltip with HTML content
 */
export function showTooltip(tooltip, html, event) {
  if (!tooltip) return;

  d3.select(tooltip)
    .style('opacity', '1')
    .style('left', `${event.clientX + 10}px`)
    .style('top', `${event.clientY - 28}px`);

  d3.select(tooltip).select('.tooltip-content').html(html);
}

/**
 * Hide tooltip
 */
export function hideTooltip(tooltip) {
  if (!tooltip) return;
  d3.select(tooltip).style('opacity', '0');
}

/**
 * Move tooltip to follow cursor
 */
export function moveTooltip(tooltip, event) {
  if (!tooltip) return;
  d3.select(tooltip)
    .style('left', `${event.clientX + 10}px`)
    .style('top', `${event.clientY - 28}px`);
}

/**
 * Format date for display
 */
export function formatDate(date, format = '%b %d') {
  return d3.timeFormat(format)(date);
}

/**
 * Create standard X axis (time-based)
 */
export function createTimeXAxis(svg, scale, height, tickCount = 6, format = '%b %d') {
  return svg.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`)
    .call(d3.axisBottom(scale)
      .tickFormat(d3.timeFormat(format))
      .ticks(tickCount))
    .style('color', 'oklch(var(--color-base-content) / 0.7)')
    .style('font-size', '12px');
}

/**
 * Create standard Y axis
 */
export function createYAxis(svg, scale, label = '') {
  const axis = svg.append('g')
    .attr('class', 'y-axis')
    .call(d3.axisLeft(scale))
    .style('color', 'oklch(var(--color-base-content) / 0.7)')
    .style('font-size', '12px');

  if (label) {
    svg.append('text')
      .attr('class', 'y-axis-label')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -scale.range()[0] / 2)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', 'oklch(var(--color-base-content) / 0.7)')
      .text(label);
  }

  return axis;
}
